import { useEffect, useMemo, useRef, useState } from 'react'
import { IForm } from './useForm'

export const useFormState = (form: IForm) => {
  const canSetState = useRef(true) // Guard for setState operations
  const [values, setValues] = useState<Record<string, any>>(form.getValues())
  const [isDirty, setIsDirty] = useState(false)
  const [dirtyFields, setDirtyFields] = useState<Record<string, any>>({})

  useEffect(() => {
    canSetState.current = true
    return () => {
      canSetState.current = false // Hook shouldn't call react setState when guard is false
    }
  }, [])

  useEffect(() => {
    const unsubscribe = form.subscribe({
      formState: { values: true, isDirty: true, dirtyFields: true },
      callback: nextForm => {
        if (!canSetState.current) {
          return
        }
        setValues(currentValues => ({ ...currentValues, ...nextForm.values }))
        if (typeof nextForm.dirtyFields !== 'undefined') {
          setDirtyFields(nextForm.dirtyFields)
        }
        if (typeof nextForm.isDirty !== 'undefined') {
          setIsDirty(nextForm.isDirty)
        }
      }
    })
    return () => unsubscribe()
  }, [form])

  return useMemo(
    () => ({
      values,
      isDirty,
      isLoading: form.isLoading,
      isSaving: form.isSaving,
      isFailed: form.isFailed,
      defaultValues: form.defaultValues,
      dirtyFields,
      isReady: form.isReady
    }),
    [dirtyFields, form.defaultValues, form.isFailed, form.isLoading, form.isReady, form.isSaving, isDirty, values]
  )
}
